/**
 * Sticky Scroll Nav Component
 *
 * Scans the page for all elements matching `.scroll-target`, extracts the
 * text of the first `h2` found inside each as the nav label, and tracks
 * which section is currently in view using an IntersectionObserver.
 *
 * Usage in HTML:
 *   Include the _StickyScrollNav.cshtml partial anywhere on the page.
 *   Mark your content sections with the `scroll-target` class and ensure
 *   each contains at least one `h2`.
 *
 * Exposed state:
 *   - items (Array): { id, label } for each discovered section
 *   - activeId (string): id of the section currently in view
 *
 * Methods:
 *   - scrollTo(id): smoothly scrolls to the section with the given id
 */
export default function stickyScrollNav() {
    return {
        items: [],
        activeId: null,
        open: false,
        visible: false,
        _observer: null,

        init() {
            this._buildItems();
            this._observe();
            this._setupCloseHandlers();
            // Slide the whole nav in from the left after page paint
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.visible = true;
                });
            });
        },

        destroy() {
            if (this._observer) {
                this._observer.disconnect();
            }
            this._cleanupCloseHandlers();
        },

        _setupCloseHandlers() {
            this._handleClickOutside = (event) => {
                if (!this.open) return;

                // Check if click is outside the nav element
                if (!this.$el.contains(event.target)) {
                    this.open = false;
                }
            };

            this._handleEscapeKey = (event) => {
                if (event.key === 'Escape' && this.open) {
                    this.open = false;
                }
            };

            document.addEventListener('click', this._handleClickOutside);
            document.addEventListener('keydown', this._handleEscapeKey);
        },

        _cleanupCloseHandlers() {
            if (this._handleClickOutside) {
                document.removeEventListener('click', this._handleClickOutside);
            }
            if (this._handleEscapeKey) {
                document.removeEventListener('keydown', this._handleEscapeKey);
            }
        },

        _buildItems() {
            const sections = document.querySelectorAll('.scroll-target');

            sections.forEach((section, index) => {
                // Ensure each section has an id to link to
                if (!section.id) {
                    const heading = section.querySelector('h2');
                    const slug = heading
                        ? heading.textContent.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
                        : `section-${index}`;
                    section.id = slug;
                }

                const h2 = section.querySelector('h2');
                const label = h2 ? h2.textContent.trim() : section.id;

                this.items.push({ id: section.id, label });
            });

            if (this.items.length > 0) {
                this.activeId = this.items[0].id;
            }
        },

        _observe() {
            if (!('IntersectionObserver' in window)) return;

            // Fire when a section crosses the top 20% of the viewport
            this._observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            this.activeId = entry.target.id;
                        }
                    });
                },
                { rootMargin: '0px 0px -80% 0px', threshold: 0 }
            );

            this.items.forEach(({ id }) => {
                const el = document.getElementById(id);
                if (el) this._observer.observe(el);
            });
        },

        scrollTo(id) {
            const el = document.getElementById(id);
            if (el) {
                const headerOffset = 185;
                const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
                window.scrollTo({ top, behavior: 'smooth' });
                this.activeId = id;
            }
        },
    };
}
